import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
export function formatDateTime(dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const options = {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    };
    return dateTime.toLocaleString('en-US', options).replace(',', ' -');
}
export const convertToLocalTimezone = (date) => {
    if (!date)
        return null;
    const currentTimezone = dayjs.tz.guess();
    const dateInLocal = dayjs(date).tz(currentTimezone);
    if (dateInLocal.isValid()) {
        return dateInLocal.format('YYYY-MM-DDTHH:mm:ss');
    }
    else {
        return null;
    }
};
export function convertUtcToLocal(utcDateString) {
    const currentTimezone = dayjs.tz.guess();
    const localTime = dayjs.utc(utcDateString).tz(currentTimezone).format('DD/MM/YYYY HH:mm');
    return localTime;
}
export const getLocalTimeFromDate = (date) => {
    if (!date)
        return null;
    const currentTimezone = dayjs.tz.guess();
    const dateInLocal = dayjs(date).tz(currentTimezone);
    if (dateInLocal.isValid()) {
        return dateInLocal.format('HH:mm:ss');
    }
    else {
        return null;
    }
};
export const getLocalTimeFromDateForEmailTemplate = (date) => {
    if (!date)
        return null;
    const currentTimezone = dayjs.tz.guess();
    const dateInLocal = dayjs(date).tz(currentTimezone);
    if (dateInLocal.isValid()) {
        return dateInLocal.format('HH:mm');
    }
    else {
        return null;
    }
};
export function formatDateToDutch(date) {
    if (!date)
        return null;
    const d = new Date(date);
    if (isNaN(d.getTime()))
        return null;
    const formattedDateString = d.toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    const [day, month, year] = formattedDateString.split('-').map(Number);
    const formattedDate = new Date(year, month - 1, day);
    return formattedDate;
}
export const formatDMY = (date) => {
    if (date !== null && date !== undefined && date !== '')
        return dayjs(date).format('DD/MM/YYYY');
    return '';
};
// Extracts "HH:mm:ss" from an ISO string or time-only string without any timezone conversion.
// Use this instead of extractTime() when the stored time must be displayed as-is (e.g. pickup times
// where the backend serialises DateTimeOffset with a server UTC offset that shouldn't shift the hour).
export const extractTimeRaw = (dateString) => {
    if (!dateString)
        return '';
    // ISO string: pull the time segment that follows "T", ignoring any offset/Z suffix
    const isoMatch = dateString.match(/T(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (isoMatch) {
        return `${isoMatch[1].padStart(2, '0')}:${isoMatch[2]}:${(isoMatch[3] ?? '00').padStart(2, '0')}`;
    }
    // Time-only string: HH:mm or HH:mm:ss
    const timeMatch = dateString.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (timeMatch) {
        return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}:${(timeMatch[3] ?? '00').padStart(2, '0')}`;
    }
    return '';
};
export const extractTime = (dateString) => {
    if (!dateString)
        return '';
    const d = new Date(dateString);
    if (!isNaN(d.getTime())) {
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${hh}:${mm}:${ss}`;
    }
    const match = dateString.match(/(\d{1,2}:\d{2}(?::\d{2})?)/);
    return match ? match[1] : '';
};
// Convert UTC ISO strings (date + time from two separate fields) to local time for display
export const convertUtcEmailTimesToLocal = (scheduledDate, scheduledTime) => {
    if (!scheduledDate || !scheduledTime) {
        return { localDate: null, localTime: null };
    }
    try {
        // scheduledDate is a pure calendar-day anchor (UTC midnight of the picked day, stored
        // verbatim by convertLocalEmailTimesToUtc) - read its date literally instead of running
        // it through a timezone conversion. Combining it with scheduledTime's real UTC hour
        // (which carries the original timezone offset) and then converting that combined
        // instant to local time double-applies the offset and shifts the date forward.
        const localDate = dayjs.utc(scheduledDate).format('YYYY-MM-DD');
        const currentTimezone = dayjs.tz.guess();
        const timeUtc = dayjs.utc(scheduledTime);
        const localTimeOfDay = dayjs.utc(`2000-01-01T${timeUtc.format('HH:mm:ss.SSS')}`).tz(currentTimezone);
        return {
            localDate,
            localTime: `2000-01-01T${localTimeOfDay.format('HH:mm:ss.SSS')}`
        };
    }
    catch (err) {
        return { localDate: null, localTime: null };
    }
};
// Convert local time (date + time from two fields) to UTC ISO strings for backend
export const convertLocalEmailTimesToUtc = (localDate, localTime) => {
    if (!localDate) {
        return { utcDate: undefined, utcTime: undefined };
    }
    try {
        const currentTimezone = dayjs.tz.guess();
        // If time is not provided, use midnight (00:00:00)
        let h = 0, m = 0, s = 0, ms = 0;
        if (localTime) {
            const timeMatch = localTime.match(/T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?/);
            if (!timeMatch) {
                return { utcDate: undefined, utcTime: undefined };
            }
            h = parseInt(timeMatch[1]);
            m = parseInt(timeMatch[2]);
            s = parseInt(timeMatch[3]);
            ms = timeMatch[4] ? parseInt(timeMatch[4]) : 0;
        }
        // Build local datetime and convert to UTC
        const localDt = dayjs.tz(`${localDate}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`, currentTimezone).millisecond(ms);
        // ScheduledDate is a pure calendar day - the backend gates sends by comparing its
        // UTC .Date component directly against utcNow.Date, with no time-of-day precision.
        // Converting the local date through timezone math (even truncated to the local day
        // first) still shifts the UTC calendar date backward for positive UTC offsets, since
        // local midnight converts to the previous UTC day. Anchor the picked date verbatim as
        // UTC midnight instead, so its date component always matches what was picked.
        const utcDate = dayjs.utc(`${localDate}T00:00:00`).toDate().toISOString();
        const utcTime = localDt.utc().year(2000).month(0).date(1).toDate().toISOString();
        return { utcDate, utcTime };
    }
    catch (err) {
        return { utcDate: undefined, utcTime: undefined };
    }
};
export const formatDMYT = (date) => {
    if (date)
        return dayjs(date).format('DD/MM/YYYY HH:mm');
    return '';
};
export const formatRDWDate = (inputDate) => {
    try {
        let date;
        // Check if the input is in the format "YYYYMMDD" (e.g., "20230127")
        if (/^\d{8}$/.test(inputDate)) {
            let year = inputDate.substring(0, 4);
            let month = inputDate.substring(4, 6) - 1;
            let day = inputDate.substring(6, 8);
            date = new Date(year, month, day);
        }
        // Check if the input is in the format "YYYY-MM-DDTHH:mm:ss.sss" (e.g., "2024-02-14T00:00:00.000")
        else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}$/.test(inputDate)) {
            date = new Date(inputDate); // This is a valid ISO string
        }
        else {
            throw new Error('Invalid date format');
        }
        let formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
        return formattedDate;
    }
    catch (error) {
        return 'N/A';
    }
};
